import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, X, Check, Send, Loader2, Trash2 } from "lucide-react";
import { fmtDateTime } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEV = { error: "border-red-500/40 bg-red-500/5", warn: "border-amber-400/40 bg-amber-400/5", info: "border-white/10 bg-[#0C0C0C]" };

export default function NotificationBell({ token }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    axios.get(`${API}/admin/notifications?limit=60`, auth)
      .then((r) => { setUnread(r.data.unread); setItems(r.data.notifications); }).catch(() => {});
    // eslint-disable-next-line
  }, [token]);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const markRead = async (id) => {
    await axios.post(`${API}/admin/notifications/${id}/read`, {}, auth).catch(() => {});
    load();
  };
  const readAll = async () => {
    await axios.post(`${API}/admin/notifications/read-all`, {}, auth).catch(() => {});
    load();
  };
  const dismiss = async (id) => {
    setItems((prev) => prev.filter((n) => n.id !== id));
    await axios.post(`${API}/admin/notifications/${id}/dismiss`, {}, auth).catch(() => {});
    load();
  };
  const clearAll = async () => {
    if (!window.confirm(`Clear ALL ${items.length} notifications? This deletes them permanently.`)) return;
    try {
      const { data } = await axios.post(`${API}/admin/notifications/clear-all`, {}, auth);
      toast.success(`${data.cleared} notification${data.cleared !== 1 ? "s" : ""} cleared.`);
      load();
    } catch { toast.error("Could not clear notifications."); }
  };
  const resend = async (id) => {
    setBusy(id);
    try {
      const { data } = await axios.post(`${API}/admin/notifications/${id}/resend`, {}, auth);
      toast.success(`Re-sent — SMS: ${data.sms_status} · Email: ${data.email_status}`);
      load();
    } catch { toast.error("Resend failed."); } finally { setBusy(""); }
  };

  return (
    <div className="relative">
      <button data-testid="admin-notifications-bell" onClick={() => setOpen((o) => !o)}
        className="relative flex items-center gap-2 text-sm border border-white/15 px-3 py-2 rounded-md hover:bg-white hover:text-black transition-colors">
        <Bell size={15} />
        {unread > 0 && (
          <span data-testid="admin-notifications-badge" className="absolute -top-1.5 -right-1.5 bg-[#F2B705] text-black text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{unread > 99 ? "99+" : unread}</span>
        )}
      </button>
      {open && (
        <div data-testid="admin-notifications-panel" className="absolute right-0 mt-2 w-[380px] max-w-[92vw] max-h-[70vh] overflow-y-auto rounded-lg border border-white/15 bg-[#0A0A0A] shadow-2xl z-50 p-3 space-y-2">
          <div className="flex items-center justify-between sticky top-0 bg-[#0A0A0A] pb-2">
            <span className="text-sm font-bold text-[#F2B705]">Notification Center</span>
            <span className="flex items-center gap-2">
              {unread > 0 && <button data-testid="notifications-read-all" onClick={readAll} className="text-[11px] text-white/50 hover:text-white">Mark all read</button>}
              {items.length > 0 && <button data-testid="notifications-clear-all" onClick={clearAll} className="text-[11px] text-red-400/80 hover:text-red-300 font-semibold">Clear all</button>}
              <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white"><X size={14} /></button>
            </span>
          </div>
          {items.length === 0 && <p className="text-white/40 text-xs text-center py-6">No notifications yet.</p>}
          {items.map((n) => (
            <div key={n.id} data-testid={`notification-${n.id}`} className={`rounded-md border p-2.5 ${SEV[n.severity] || SEV.info} ${n.read ? "opacity-55" : ""}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="text-xs font-bold text-white/90">{n.title}</div>
                <span className="flex items-center gap-1.5 shrink-0">
                  {!n.read && <button data-testid={`notification-read-${n.id}`} onClick={() => markRead(n.id)} title="Mark read" className="text-white/40 hover:text-emerald-400"><Check size={13} /></button>}
                  <button data-testid={`notification-dismiss-${n.id}`} onClick={() => dismiss(n.id)} title="Dismiss — remove this notification" className="text-white/40 hover:text-red-400"><Trash2 size={13} /></button>
                </span>
              </div>
              <div className="text-[11px] text-white/55 whitespace-pre-line mt-1">{n.body}</div>
              <div className="flex items-center justify-between mt-1.5">
                <span className="text-[10px] text-white/30">{fmtDateTime(n.created_at)}
                  {n.sms_status && <> · SMS {n.sms_status.startsWith("sent") ? "✓" : n.sms_status.startsWith("failed") ? "✗" : "—"}</>}
                  {n.email_status && <> · Email {n.email_status.startsWith("sent") ? "✓" : n.email_status.startsWith("failed") ? "✗" : "—"}</>}
                </span>
                {(String(n.sms_status).startsWith("failed") || String(n.email_status).startsWith("failed")) && (
                  <button data-testid={`notification-resend-${n.id}`} onClick={() => resend(n.id)} disabled={busy === n.id}
                    className="text-[10px] font-bold text-[#F2B705] flex items-center gap-1 hover:underline disabled:opacity-50">
                    {busy === n.id ? <Loader2 className="animate-spin" size={10} /> : <Send size={10} />} Resend
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
